import { HStack, VStack, Flex, Box } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import MonthSelector from "./MonthSelector";
import type { JobStatusValues, MonthSelectorState } from "@models/Schedule";
import { getDateList, getFormatDateWithEndTime, getFormatDateWithStartTime } from "@utils/dateUtils";
import LoadingIndicator from "@components/Loading";
import ShiftList from "./ShiftList";
import JobSummaryList from "./JobSummaryList";
import MonthDateItem from "./MonthDateItem";
import { getCompetenceLabelByKey } from "@utils/utils";
import { useJobSchedule, useOrganisation } from "@api/hooks";
import { scrollToToday } from "./scrollToToday";

interface Props {
	selectedUnits: string[];
	dateValues: MonthSelectorState;
	setDateValues: React.Dispatch<React.SetStateAction<MonthSelectorState>>;
}

const JobView = ({ selectedUnits, dateValues, setDateValues }: Props) => {
	const { organisation } = useOrganisation();
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const didInitialScrollRef = useRef(false);

	const { jobSchedules, isLoading } = useJobSchedule(
		getFormatDateWithStartTime(dateValues.startDate),
		getFormatDateWithEndTime(dateValues.endDate),
		selectedUnits,
		["customer", "applications.consultant"],
		["applications.consultant.absences", "applications.consultant.hasWorked"],
	);

	const competenceLabelByKey = getCompetenceLabelByKey(organisation?.competences ?? []);
	const monthDateList = getDateList(dateValues.startDate, dateValues.endDate) ?? [];

	useEffect(() => {
		if (isLoading || didInitialScrollRef.current || !scrollRef.current) return;
		scrollToToday(scrollRef.current, monthDateList);
		didInitialScrollRef.current = true;
	}, [isLoading, monthDateList]);

	const handlePrevMonth = () => {
		setDateValues((prevState) => {
			const newStartDate = new Date(prevState.startDate.getFullYear(), prevState.startDate.getMonth() - 1, 1);
			const newEndDate = new Date(prevState.endDate.getFullYear(), prevState.endDate.getMonth(), 0);
			return { startDate: newStartDate, endDate: newEndDate };
		});
	};

	const handleNextMonth = () => {
		setDateValues((prevState) => {
			const newStartDate = new Date(prevState.startDate.getFullYear(), prevState.startDate.getMonth() + 1, 1);
			const newEndDate = new Date(prevState.endDate.getFullYear(), prevState.endDate.getMonth() + 2, 0);
			return { startDate: newStartDate, endDate: newEndDate };
		});
	};

	return (
		<Flex
			direction="row"
			height={"800px"}
			width={"100%"}
			maxWidth={{ base: "1000px", "2xl": "none" }}
			backgroundColor={"#FFFFFF"}
			borderRadius={"lg"}
			alignItems="flex-start"
			overflowY="auto"
		>
			<Box width="290px" flexShrink={0} position={"relative"}>
				<MonthSelector value={dateValues} handleNextMonth={handleNextMonth} handlePrevMonth={handlePrevMonth} />
				<VStack borderRight={"1px solid #DEDEDE"} borderBottom={"1px solid #DEDEDE"} gap={0}>
					{!isLoading &&
						selectedUnits.length > 0 &&
						jobSchedules?.map((job) => (
							<JobSummaryList key={job.id} job={job} competenceLabelByKey={competenceLabelByKey} />
						))}
				</VStack>
			</Box>
			<Box ref={scrollRef} flex={1} minWidth={0} overflowX="auto" position={"relative"}>
				<HStack position={"sticky"} top={"0"} alignItems={"center"} height={"52.25px"} gap={"0"}>
					{monthDateList.map((dateItem) => (
						<MonthDateItem key={dateItem.date} dateItem={dateItem} />
					))}
				</HStack>
				{isLoading && <LoadingIndicator size={"s"} />}
				{!isLoading &&
					selectedUnits.length > 0 &&
					jobSchedules?.map((job) => (
						<ShiftList
							key={job.id}
							startDate={dateValues.startDate}
							endDate={dateValues.endDate}
							shiftItems={job.shifts}
							isJobView={true}
							jobStatus={job.status as JobStatusValues}
							jobApplications={job.applications}
						/>
					))}
			</Box>
		</Flex>
	);
};

export default JobView;
